/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\VoicemailGeneralBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Voicemail Settings module.
----------------------------------------------------------------*/


import { Fragment } from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { LanguageSelector } from '../../extensions/components/LanguageSelector';
import { useI18n } from '../../../hooks/useI18n';
import { VoicemailSettingsFormData, DEFAULT_FORM_DATA } from '../types';
import { EMAIL_DATE_FORMAT_OPTIONS, selectorValueFromLocale, localeFromSelectorValue } from '../voicemailSettingsUtils';

const SELECT_CLS = 'w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary';

interface Props {
    formData: VoicemailSettingsFormData;
    errors: Record<string, string>;
    isLoading: boolean;
    onNumberChange: (field: keyof VoicemailSettingsFormData) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFieldChange: (field: keyof VoicemailSettingsFormData, value: unknown) => void;
}

type NumField = { field: keyof VoicemailSettingsFormData; min: number; ph: string; ttKey: string; lblKey: string; req?: boolean };

export function VoicemailGeneralBox({ formData, errors, isLoading, onNumberChange, onFieldChange }: Props) {
    const { t } = useI18n();

    const numFields: NumField[] = [
        { field: 'maxMessageLength', min: 1, ph: '180', ttKey: 'voicemailSettings.fields.maxMessageLength.tooltip', lblKey: 'voicemailSettings.fields.maxMessageLength.label', req: true },
        { field: 'minMessageLength', min: 0, ph: '5',   ttKey: 'voicemailSettings.fields.minMessageLength.tooltip', lblKey: 'voicemailSettings.fields.minMessageLength.label' },
        { field: 'greetingsLength',  min: 1, ph: '60',  ttKey: 'voicemailSettings.fields.greetingsLength.tooltip',  lblKey: 'voicemailSettings.fields.greetingsLength.label' },
        { field: 'maxSilence',       min: 1, ph: '10',  ttKey: 'voicemailSettings.fields.maxSilence.tooltip',       lblKey: 'voicemailSettings.fields.maxSilence.label' },
        { field: 'maxLoginAttempts', min: 1, ph: '3',   ttKey: 'voicemailSettings.fields.maxLoginAttempts.tooltip', lblKey: 'voicemailSettings.fields.maxLoginAttempts.label' },
        { field: 'backupDeleted',    min: 0, ph: '100', ttKey: 'voicemailSettings.fields.backupDeleted.tooltip',    lblKey: 'voicemailSettings.fields.backupDeleted.label' },
        { field: 'maxMessages',      min: 1, ph: '9999',ttKey: 'voicemailSettings.fields.maxMessages.tooltip',      lblKey: 'voicemailSettings.fields.maxMessages.label' },
    ];

    return (
        <FormBox title={t('voicemailSettings.sections.general')} description={t('voicemailSettings.sections.generalDescription')} icon={<SettingsIcon className="h-5 w-5" />} isLoading={isLoading}>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-6 gap-y-4 items-center">
                {numFields.map(({ field, min, ph, ttKey, lblKey, req }) => (
                    <Fragment key={field}>
                        <SimpleTooltip content={t(ttKey)} className="col-span-2">
                            <Label htmlFor={field as string} className="text-left">
                                {t(lblKey)}{req && <span className="ml-1 text-destructive">*</span>}
                            </Label>
                        </SimpleTooltip>
                        <div className="col-span-2">
                            <Input id={field as string} type="number" min={min} value={formData[field] as number} onChange={onNumberChange(field)} placeholder={ph} error={errors[field as string]} />
                        </div>
                    </Fragment>
                ))}

                {/* Locale */}
                <SimpleTooltip content={t('voicemailSettings.fields.locale.tooltip')} className="col-span-2">
                    <Label htmlFor="locale" className="text-left">{t('voicemailSettings.fields.locale.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <LanguageSelector value={selectorValueFromLocale(formData.locale)} onChange={(v) => onFieldChange('locale', localeFromSelectorValue(v, DEFAULT_FORM_DATA.locale))} showVoiceSelector={false} showLabel={false} className="w-full" />
                </div>

                {/* Email Date Format */}
                <SimpleTooltip content={t('voicemailSettings.fields.emailDateFormat.tooltip')} className="col-span-2">
                    <Label htmlFor="emailDateFormat" className="text-left">{t('voicemailSettings.fields.emailDateFormat.label')}</Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <select id="emailDateFormat" value={formData.emailDateFormat} onChange={(e) => onFieldChange('emailDateFormat', e.target.value)} className={SELECT_CLS}>
                        {EMAIL_DATE_FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {errors.emailDateFormat && <p className="text-xs text-destructive mt-1">{errors.emailDateFormat}</p>}
                </div>
            </div>
        </FormBox>
    );
}
